import React, { useState, useEffect } from 'react';
import { 
  Users, MessageSquare, Phone, Mail, Calendar, Clock, Filter, Search, 
  Plus, ChevronRight, Send, Copy, Check, X, AlertTriangle, TrendingUp,
  User, Building, Plane, DollarSign, Edit2, Trash2, RefreshCw, Settings,
  Smartphone, CheckCircle, XCircle, Lock, LogOut, Package
} from 'lucide-react';
import PackageManager from './PackageManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const statusColors = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  quoted: 'bg-purple-100 text-purple-800',
  negotiating: 'bg-orange-100 text-orange-800',
  booked: 'bg-green-100 text-green-800',
  lost: 'bg-red-100 text-red-800'
};

// Source badge colors
const sourceColors = {
  whatsapp: 'bg-green-100 text-green-800',
  website: 'bg-blue-100 text-blue-800',
  phone: 'bg-purple-100 text-purple-800',
  walkin: 'bg-gray-100 text-gray-800'
};

// Priority colors
const priorityColors = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-gray-100 text-gray-800'
};

const LeadsCRM = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('leads'); // 'leads' or 'packages'

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [templates, setTemplates] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showWhatsAppConfig, setShowWhatsAppConfig] = useState(false);
  const [whatsappConfig, setWhatsappConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', source: '', search: '' });
  const [newNote, setNewNote] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [copiedTemplate, setCopiedTemplate] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setAuthLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/admin/verify`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        
        if (data.valid) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('admin_token');
        }
      } catch (error) {
        console.error('Auth check error:', error);
        localStorage.removeItem('admin_token');
      }
      setAuthLoading(false);
    };

    checkAuth();
  }, []);

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('admin_token', data.token);
        setIsAuthenticated(true);
        setLoginForm({ username: '', password: '' });
      } else {
        setLoginError(data.detail || 'Invalid username or password');
      }
    } catch (error) {
      setLoginError('Connection error. Please try again.');
    }
  };

  // Logout handler
  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
  };

  // New lead form
  const [newLead, setNewLead] = useState({
    name: '',
    mobile: '',
    email: '',
    source: 'whatsapp',
    initial_message: '',
    departure_city: '',
    travel_month: '',
    passengers: 0,
    budget: ''
  });

  // Fetch leads
  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.source) params.append('source', filters.source);
      if (filters.search) params.append('search', filters.search);
      
      const response = await fetch(`${API_URL}/api/leads/?${params}`);
      const data = await response.json();
      setLeads(data.leads || []);
      setStats(data.stats || {});
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/templates/all`);
      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  // Fetch WhatsApp config status
  const fetchWhatsAppConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/config/status`);
      const data = await response.json();
      setWhatsappConfig(data);
    } catch (error) {
      console.error('Error fetching WhatsApp config:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
      fetchTemplates();
      fetchWhatsAppConfig();
    }
  }, [filters, isAuthenticated]);

  // Create new lead
  const handleCreateLead = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLead)
      });
      if (response.ok) {
        setShowNewLeadModal(false);
        setNewLead({
          name: '', mobile: '', email: '', source: 'whatsapp',
          initial_message: '', departure_city: '', travel_month: '',
          passengers: 0, budget: ''
        });
        fetchLeads();
      }
    } catch (error) {
      console.error('Error creating lead:', error);
    }
  };

  // Update lead status
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchLeads();
      if (selectedLead?.lead_id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  // Add note
  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedLead) return;
    try {
      await fetch(`${API_URL}/api/leads/${selectedLead.lead_id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newNote, created_by: 'Admin' })
      });
      setNewNote('');
      // Refresh lead data
      const response = await fetch(`${API_URL}/api/leads/${selectedLead.lead_id}`);
      const data = await response.json();
      setSelectedLead(data);
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  // Add message
  const handleAddMessage = async (direction = 'outbound') => {
    if (!newMessage.trim() || !selectedLead) return;
    try {
      await fetch(`${API_URL}/api/leads/${selectedLead.lead_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newMessage, direction })
      });
      setNewMessage('');
      // Refresh lead data
      const response = await fetch(`${API_URL}/api/leads/${selectedLead.lead_id}`);
      const data = await response.json();
      setSelectedLead(data);
    } catch (error) {
      console.error('Error adding message:', error);
    }
  };

  // Copy template
  const copyTemplate = (content) => {
    navigator.clipboard.writeText(content);
    setCopiedTemplate(content);
    setTimeout(() => setCopiedTemplate(null), 2000);
  };

  // Apply template as message
  const applyTemplate = (content) => {
    setNewMessage(content);
    setShowTemplatesModal(false);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
          {/* Header */}
          <div className="bg-[#0a1628] p-6 text-center">
            <div className="w-16 h-16 bg-[#d4af37] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#0a1628]" />
            </div>
            <h1 className="text-2xl font-bold text-white">Admin Login</h1>
            <p className="text-gray-400 mt-1">ZamZam Umrah CRM</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="p-6 space-y-4">
            {loginError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {loginError}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37]"
                placeholder="Enter username"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37]"
                placeholder="Enter password"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-[#d4af37] text-[#0a1628] rounded-lg font-bold hover:bg-[#c9a432] transition-colors flex items-center justify-center gap-2"
            >
              <Lock className="w-5 h-5" />
              Sign In
            </button>
          </form>

          {/* Footer */}
          <div className="px-6 pb-6 text-center text-sm text-gray-500">
            Secure Admin Access Only
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#0a1628] text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-[#d4af37]" />
            <div>
              <h1 className="text-xl font-bold">Admin Panel</h1>
              <p className="text-gray-400 text-sm">ZamZam Umrah Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* WhatsApp Status */}
            <button
              onClick={() => setShowWhatsAppConfig(true)}
              className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${
                whatsappConfig?.configured 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-gray-600 hover:bg-gray-700'
              }`}
            >
              <Smartphone className="w-4 h-4" />
              WhatsApp {whatsappConfig?.configured ? 'Active' : 'Setup'}
            </button>
            {activeTab === 'leads' && (
              <button
                onClick={() => setShowNewLeadModal(true)}
                className="px-4 py-2 bg-[#d4af37] text-[#0a1628] rounded-lg font-semibold flex items-center gap-2 hover:bg-[#c9a432] transition-colors"
              >
                <Plus className="w-5 h-5" />
                New Lead
              </button>
            )}
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto mt-4">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('leads')}
              className={`px-6 py-2 rounded-t-lg font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'leads'
                  ? 'bg-gray-100 text-[#0a1628]'
                  : 'bg-[#0a1628]/50 text-gray-300 hover:bg-[#0a1628]/70'
              }`}
            >
              <Users className="w-4 h-4" />
              Leads CRM
            </button>
            <button
              onClick={() => setActiveTab('packages')}
              className={`px-6 py-2 rounded-t-lg font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'packages'
                  ? 'bg-gray-100 text-[#0a1628]'
                  : 'bg-[#0a1628]/50 text-gray-300 hover:bg-[#0a1628]/70'
              }`}
            >
              <Package className="w-4 h-4" />
              Packages
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'packages' ? (
        <PackageManager />
      ) : (
        <div className="max-w-7xl mx-auto p-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl font-bold text-gray-800">{stats.total || 0}</div>
              <div className="text-sm text-gray-500">Total Leads</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 shadow-sm border-l-4 border-blue-500">
              <div className="text-2xl font-bold text-blue-800">{stats.new || 0}</div>
              <div className="text-sm text-blue-600">New</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 shadow-sm border-l-4 border-yellow-500">
            <div className="text-2xl font-bold text-yellow-800">{stats.contacted || 0}</div>
            <div className="text-sm text-yellow-600">Contacted</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 shadow-sm border-l-4 border-purple-500">
            <div className="text-2xl font-bold text-purple-800">{stats.quoted || 0}</div>
            <div className="text-sm text-purple-600">Quoted</div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 shadow-sm border-l-4 border-orange-500">
            <div className="text-2xl font-bold text-orange-800">{stats.negotiating || 0}</div>
            <div className="text-sm text-orange-600">Negotiating</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 shadow-sm border-l-4 border-green-500">
            <div className="text-2xl font-bold text-green-800">{stats.booked || 0}</div>
            <div className="text-sm text-green-600">Booked</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4 shadow-sm border-l-4 border-red-500">
            <div className="text-2xl font-bold text-red-800">{stats.lost || 0}</div>
            <div className="text-sm text-red-600">Lost</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or mobile..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37]"
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="quoted">Quoted</option>
              <option value="negotiating">Negotiating</option>
              <option value="booked">Booked</option>
              <option value="lost">Lost</option>
            </select>
            <select
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
            >
              <option value="">All Sources</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="website">Website</option>
              <option value="phone">Phone</option>
              <option value="walkin">Walk-in</option>
            </select>
            <button
              onClick={() => setShowTemplatesModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center gap-2 hover:bg-green-700"
            >
              <MessageSquare className="w-4 h-4" />
              Quick Replies
            </button>
            <button
              onClick={fetchLeads}
              className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Leads List */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-3 bg-gray-50 border-b font-semibold text-gray-700">
              Leads ({leads.length})
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Loading...</div>
              ) : leads.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No leads found</div>
              ) : (
                leads.map((lead) => (
                  <div
                    key={lead.lead_id}
                    onClick={() => setSelectedLead(lead)}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedLead?.lead_id === lead.lead_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800 truncate">{lead.name}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {lead.mobile}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[lead.status]}`}>
                          {lead.status}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${sourceColors[lead.source]}`}>
                          {lead.source}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(lead.created_at)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Lead Details */}
          <div className="lg:col-span-2">
            {selectedLead ? (
              <div className="bg-white rounded-lg shadow-sm">
                {/* Lead Header */}
                <div className="p-4 bg-gray-50 border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">{selectedLead.name}</h2>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {selectedLead.mobile}
                        </span>
                        {selectedLead.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {selectedLead.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[selectedLead.status]}`}>
                        {selectedLead.status}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm ${sourceColors[selectedLead.source]}`}>
                        {selectedLead.source}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Status Actions */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="text-sm font-medium text-gray-600 mb-2">Update Status:</div>
                  <div className="flex flex-wrap gap-2">
                    {['new', 'contacted', 'quoted', 'negotiating', 'booked', 'lost'].map((status) => (
                      <button
                        key={status}
                        onClick={() => updateLeadStatus(selectedLead.lead_id, status)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          selectedLead.status === status
                            ? statusColors[status]
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Lead Info Grid */}
                <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-b">
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Departure City</div>
                    <div className="font-medium">{selectedLead.departure_city || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Travel Month</div>
                    <div className="font-medium">{selectedLead.travel_month || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Passengers</div>
                    <div className="font-medium">{selectedLead.passengers || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Budget</div>
                    <div className="font-medium">{selectedLead.budget || 'N/A'}</div>
                  </div>
                </div>

                {/* Conversation History */}
                <div className="p-4 border-b">
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Conversation History
                  </h3>
                  <div className="max-h-[200px] overflow-y-auto space-y-2 mb-4">
                    {selectedLead.messages?.length > 0 ? (
                      selectedLead.messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg ${
                            msg.direction === 'inbound'
                              ? 'bg-gray-100 mr-8'
                              : 'bg-blue-100 ml-8'
                          }`}
                        >
                          <div className="text-sm">{msg.content}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {msg.direction === 'inbound' ? '← Customer' : '→ Sent'} · {formatDate(msg.timestamp)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500">No messages yet</div>
                    )}
                  </div>
                  
                  {/* Add Message */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddMessage('outbound')}
                    />
                    <button
                      onClick={() => handleAddMessage('inbound')}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      title="Log received message"
                    >
                      ← In
                    </button>
                    <button
                      onClick={() => handleAddMessage('outbound')}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"
                      title="Log sent message"
                    >
                      <Send className="w-4 h-4" />
                      Out
                    </button>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Edit2 className="w-4 h-4" />
                    Notes & Follow-up
                  </h3>
                  <div className="max-h-[150px] overflow-y-auto space-y-2 mb-4">
                    {selectedLead.notes?.length > 0 ? (
                      selectedLead.notes.map((note, idx) => (
                        <div key={idx} className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="text-sm">{note.content}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {note.created_by} · {formatDate(note.created_at)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500">No notes yet</div>
                    )}
                  </div>
                  
                  {/* Add Note */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add a note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                    />
                    <button
                      onClick={handleAddNote}
                      className="px-4 py-2 bg-[#d4af37] text-[#0a1628] rounded-lg font-semibold hover:bg-[#c9a432]"
                    >
                      Add Note
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
                <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p>Select a lead to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* New Lead Modal */}
      {showNewLeadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold">Add New Lead</h2>
              <button onClick={() => setShowNewLeadModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateLead} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  required
                  value={newLead.name}
                  onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile *</label>
                <input
                  type="tel"
                  required
                  value={newLead.mobile}
                  onChange={(e) => setNewLead({ ...newLead, mobile: e.target.value })}
                  placeholder="+44 7XXX XXXXXX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <select
                  value={newLead.source}
                  onChange={(e) => setNewLead({ ...newLead, source: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                >
                  <option value="whatsapp">WhatsApp</option>
                  <option value="website">Website</option>
                  <option value="phone">Phone</option>
                  <option value="walkin">Walk-in</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Initial Message</label>
                <textarea
                  value={newLead.initial_message}
                  onChange={(e) => setNewLead({ ...newLead, initial_message: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Departure City</label>
                  <input
                    type="text"
                    value={newLead.departure_city}
                    onChange={(e) => setNewLead({ ...newLead, departure_city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Travel Month</label>
                  <input
                    type="text"
                    value={newLead.travel_month}
                    onChange={(e) => setNewLead({ ...newLead, travel_month: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Passengers</label>
                  <input
                    type="number"
                    value={newLead.passengers}
                    onChange={(e) => setNewLead({ ...newLead, passengers: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget</label>
                  <input
                    type="text"
                    value={newLead.budget}
                    onChange={(e) => setNewLead({ ...newLead, budget: e.target.value })}
                    placeholder="e.g. £1000-1500"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37]"
                  />
                </div>
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-[#d4af37] text-[#0a1628] rounded-lg font-bold hover:bg-[#c9a432]"
              >
                Create Lead
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Quick Reply Templates Modal */}
      {showTemplatesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-600" />
                Quick Reply Templates
              </h2>
              <button onClick={() => setShowTemplatesModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {templates.map((template) => (
                <div key={template.template_id} className="border rounded-lg p-4 hover:border-[#d4af37] transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-800">{template.title}</h3>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                        {template.category}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => copyTemplate(template.content)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
                        title="Copy to clipboard"
                      >
                        {copiedTemplate === template.content ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      {selectedLead && (
                        <button
                          onClick={() => applyTemplate(template.content)}
                          className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                        >
                          Use
                        </button>
                      )}
                    </div>
                  </div>
                  <pre className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                    {template.content}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Configuration Modal */}
      {showWhatsAppConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-green-600" />
                WhatsApp Integration
              </h2>
              <button onClick={() => setShowWhatsAppConfig(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Status */}
              <div className={`p-4 rounded-lg ${whatsappConfig?.configured ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {whatsappConfig?.configured ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-yellow-600" />
                  )}
                  <span className="font-semibold">
                    {whatsappConfig?.configured ? 'WhatsApp API Connected' : 'WhatsApp API Not Configured'}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {whatsappConfig?.configured 
                    ? 'Auto-reply is enabled. Incoming WhatsApp messages will create leads automatically.'
                    : 'Configure WhatsApp Business API to enable auto-replies and automatic lead capture.'
                  }
                </p>
              </div>

              {/* Business Phone */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">Business Phone Number</div>
                <div className="font-semibold text-lg">{whatsappConfig?.business_phone || '+44 1582 800304'}</div>
              </div>

              {/* Webhook Info */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-medium text-blue-800 mb-2">Webhook Configuration</div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">Webhook URL:</span>
                    <code className="ml-2 bg-white px-2 py-1 rounded text-xs">
                      {window.location.origin}/api/whatsapp/webhook
                    </code>
                  </div>
                  <div>
                    <span className="text-gray-600">Verify Token:</span>
                    <code className="ml-2 bg-white px-2 py-1 rounded text-xs">
                      {whatsappConfig?.verify_token || 'zamzam_umrah_webhook_2026'}
                    </code>
                  </div>
                </div>
              </div>

              {/* Setup Instructions */}
              {!whatsappConfig?.configured && whatsappConfig?.setup_instructions && (
                <div className="space-y-3">
                  <div className="font-semibold text-gray-700">Setup Instructions:</div>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                    {Object.values(whatsappConfig.setup_instructions).map((step, idx) => (
                      <li key={idx} className="pl-2">{step}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Test Button */}
              <div className="pt-4 border-t">
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API_URL}/api/whatsapp/test-webhook`, { method: 'POST' });
                      const data = await response.json();
                      alert(data.message || 'Test webhook sent! Check CRM for new lead.');
                      fetchLeads();
                    } catch (error) {
                      alert('Error testing webhook');
                    }
                  }}
                  className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  🧪 Test Webhook (Create Test Lead)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsCRM;
